import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plane, Search, ExternalLink, Loader2, ArrowRight, Users, Calendar,
  Clock, Car, TrendingDown, CheckCircle2, XCircle, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FlugSuche-Komponente – Swiss Premium Look
 * - Duffel API: Live-Flugsuche mit echten Preisen
 * - PKW-Vergleich: Kosten & Dauer nebeneinander
 * - Price-Sync: Flug als Reisekosten-Position übernehmen
 * - Booking-Readiness: Chef-Freigabe vor Buchung
 * - Fallback: Kiwi.com Deep-Link
 */
export const FlugSuche = ({ tripId, startOrt, zielOrt, kmSatz, onSelectFlight }) => {
  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState("");
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [selectedTo, setSelectedTo] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [flightType, setFlightType] = useState("oneway");
  const [adults, setAdults] = useState(1);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchMode, setSearchMode] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showFromDrop, setShowFromDrop] = useState(false);
  const [showToDrop, setShowToDrop] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const debounceRef = useRef(null);

  // Autocomplete
  const searchLocations = useCallback(async (term, setter) => {
    if (term.length < 2) { setter([]); return; }
    try {
      const res = await axios.get(`${API}/reise/flights/locations`, { params: { term } });
      setter(res.data || []);
    } catch { setter([]); }
  }, []);

  const handleFromChange = (val) => {
    setFromQuery(val); setSelectedFrom(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { searchLocations(val, setFromSuggestions); setShowFromDrop(true); }, 300);
  };
  const handleToChange = (val) => {
    setToQuery(val); setSelectedTo(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { searchLocations(val, setToSuggestions); setShowToDrop(true); }, 300);
  };
  const selectFrom = (loc) => { setSelectedFrom(loc); setFromQuery(`${loc.name} (${loc.code})`); setFromSuggestions([]); setShowFromDrop(false); };
  const selectTo = (loc) => { setSelectedTo(loc); setToQuery(`${loc.name} (${loc.code})`); setToSuggestions([]); setShowToDrop(false); };

  useEffect(() => {
    const handler = (e) => {
      if (fromRef.current && !fromRef.current.contains(e.target)) setShowFromDrop(false);
      if (toRef.current && !toRef.current.contains(e.target)) setShowToDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Hilfsfunktionen
  const formatDate = (isoDate) => { if (!isoDate) return ""; const [y, m, d] = isoDate.split("-"); return `${d}/${m}/${y}`; };
  const fmtTime = (isoStr) => { if (!isoStr) return ""; try { return new Date(isoStr).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const fmtDuration = (dur) => {
    if (!dur) return "";
    const match = String(dur).match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (match) { const h = parseInt(match[1] || 0); const m = parseInt(match[2] || 0); return h > 0 ? `${h}h ${m}m` : `${m}m`; }
    return dur;
  };
  const fmtPrice = (amount, currency = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(parseFloat(amount));
  const fmtTimestamp = (isoStr) => {
    if (!isoStr) return "–";
    const date = new Date(isoStr);
    return Number.isNaN(date.getTime()) ? "–" : date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  };
  const tripScope = flightType === "round" ? "Hin- und Rückflug" : "Hinflug";

  // Flugsuche
  const handleSearch = async () => {
    if (!selectedFrom || !selectedTo) { toast.error("Bitte Abflugort und Zielort auswählen"); return; }
    if (!dateFrom) { toast.error("Bitte Reisedatum angeben"); return; }
    if (flightType === "round" && !returnDate) { toast.error("Bitte Rückflugdatum angeben"); return; }
    setSearching(true); setResults(null); setSearchMode(null); setSelectedOffer(null);

    // 1. Duffel (Live-Preise + PKW-Vergleich)
    try {
      const duffelPayload = {
        origin: selectedFrom.code || selectedFrom.id,
        destination: selectedTo.code || selectedTo.id,
        departure_date: dateFrom,
        return_date: flightType === "round" ? returnDate : "",
        adults, children: 0, cabin_class: "economy",
        start_ort: startOrt || fromQuery.replace(/\s*\(.*\)/, ""),
        ziel_ort: zielOrt || toQuery.replace(/\s*\(.*\)/, ""),
        km_satz: kmSatz || 0.30,
        fahrtart: flightType === "round" ? "hin_und_rueck" : "einfach",
        trip_id: tripId || "",
      };
      const res = await axios.post(`${API}/reise/flights/duffel/search`, duffelPayload);
      if (res.data && res.data.offers && res.data.offers.length > 0) {
        setResults(res.data); setSearchMode("duffel");
        toast.success(`${res.data.shown_offers} Duffel-Angebote gefunden.`);
        setSearching(false); return;
      }
      // Falls Duffel Fehler hat aber PKW-Vergleich existiert, trotzdem anzeigen
      if (res.data && res.data.pkw_vergleich && !res.data.pkw_vergleich.error) {
        setResults(res.data); setSearchMode("duffel");
        setSearching(false); return;
      }
    } catch (e) { console.log("Duffel nicht verfügbar, Fallback zu Kiwi", e); }

    // 2. Fallback: Kiwi
    try {
      const kiwiPayload = {
        fly_from: selectedFrom.code || selectedFrom.id,
        fly_to: selectedTo.code || selectedTo.id,
        fly_from_name: selectedFrom.slug || "", fly_to_name: selectedTo.slug || "",
        date_from: formatDate(dateFrom), return_from: flightType === "round" ? formatDate(returnDate) : "",
        adults, flight_type: flightType,
      };
      const res = await axios.post(`${API}/reise/flights/search`, kiwiPayload);
      setResults(res.data); setSearchMode("kiwi");
      toast.success("Flugsuche bereit");
    } catch (e) { toast.error(e.response?.data?.detail || "Flugsuche fehlgeschlagen"); }
    setSearching(false);
  };

  // Flug übernehmen (Price-Sync)
  const handleSelectFlight = async (offer) => {
    setSelectedOffer(offer);
    const firstSeg = offer.segments?.[0];
    const lastSeg = offer.segments?.[offer.segments.length - 1];
    // Hinflug-Ziel ist das letzte Segment des Hinflugs (nicht Rückflug)
    const outboundLastSeg = offer.segments?.[offer.segments.length - 1];

    if (tripId) {
      try {
        await axios.post(`${API}/reise/flights/duffel/select`, {
          trip_id: tripId,
          offer_id: offer.id || "",
          airline: firstSeg?.airline_name || "",
          flight_number: firstSeg?.flight_number || "",
          origin: firstSeg?.origin || "",
          destination: outboundLastSeg?.destination || "",
          departure_time: firstSeg?.departure_time || "",
          arrival_time: lastSeg?.arrival_time || "",
          price: parseFloat(offer.price),
          currency: offer.currency || "EUR",
          booking_status: "vorgemerkt",
        });
        toast.success("Flug als Reisekosten übernommen – noch nicht gebucht.");
      } catch (e) { toast.error("Fehler beim Speichern: " + (e.response?.data?.detail || e.message)); }
    }

    if (onSelectFlight) {
      onSelectFlight({
        price: parseFloat(offer.price), currency: offer.currency,
        airline: firstSeg?.airline_name || "", flight_number: firstSeg?.flight_number || "",
        from: firstSeg?.origin || "", to: outboundLastSeg?.destination || "",
        departure: firstSeg?.departure_time || "", arrival: lastSeg?.arrival_time || "",
      });
    }
  };

  return (
    <Card className="p-6 border-[var(--brand-border)] bg-white" data-testid="flug-suche-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2 text-base">
          <Plane className="w-5 h-5" /> Flugsuche & Reisekostenvergleich
        </h3>
        <span className="text-xs px-2.5 py-1 rounded-full bg-[#f5f0eb] text-[var(--brand-ink)] font-medium tracking-wide">
          FLUGSUCHE
        </span>
      </div>

      {/* Suchformular */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        {/* Von */}
        <div className="relative" ref={fromRef}>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)]">Abflugort</Label>
          <Input value={fromQuery} onChange={(e) => handleFromChange(e.target.value)}
            onFocus={() => fromSuggestions.length > 0 && setShowFromDrop(true)}
            placeholder="z. B. Hannover, Frankfurt..." className="mt-1 border-[var(--brand-border)] focus:border-[var(--brand-ink)]" />
          {showFromDrop && fromSuggestions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[var(--brand-border)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {fromSuggestions.map((loc) => (
                <button key={loc.id} onClick={() => selectFrom(loc)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#f5f0eb] transition flex justify-between items-center border-b border-[var(--brand-border)] last:border-0">
                  <span className="font-medium text-[var(--brand-ink)]">{loc.name}</span>
                  <span className="text-xs text-[var(--brand-muted)] font-mono">{loc.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Nach */}
        <div className="relative" ref={toRef}>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)]">Zielort</Label>
          <Input value={toQuery} onChange={(e) => handleToChange(e.target.value)}
            onFocus={() => toSuggestions.length > 0 && setShowToDrop(true)}
            placeholder="z. B. Zürich, London..." className="mt-1 border-[var(--brand-border)] focus:border-[var(--brand-ink)]" />
          {showToDrop && toSuggestions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[var(--brand-border)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {toSuggestions.map((loc) => (
                <button key={loc.id} onClick={() => selectTo(loc)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#f5f0eb] transition flex justify-between items-center border-b border-[var(--brand-border)] last:border-0">
                  <span className="font-medium text-[var(--brand-ink)]">{loc.name}</span>
                  <span className="text-xs text-[var(--brand-muted)] font-mono">{loc.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Datum, Flugtyp, Passagiere */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)]">Flugtyp</Label>
          <select value={flightType} onChange={(e) => setFlightType(e.target.value)}
            className="w-full h-9 mt-1 px-3 rounded-md border border-[var(--brand-border)] bg-white text-sm focus:border-[var(--brand-ink)] outline-none">
            <option value="oneway">Nur Hinflug</option>
            <option value="round">Hin & Rück</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)] flex items-center gap-1"><Calendar className="w-3 h-3" /> Hinreise</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 border-[var(--brand-border)]" />
        </div>
        <div>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)] flex items-center gap-1"><Calendar className="w-3 h-3" /> Rückreise</Label>
          <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} disabled={flightType === "oneway"} className="mt-1 border-[var(--brand-border)]" />
        </div>
        <div>
          <Label className="text-xs font-medium uppercase tracking-wider text-[var(--brand-muted)] flex items-center gap-1"><Users className="w-3 h-3" /> Reisende</Label>
          <Input type="number" min="1" max="9" value={adults} onChange={(e) => setAdults(parseInt(e.target.value) || 1)} className="mt-1 border-[var(--brand-border)]" />
        </div>
      </div>

      {/* Suche-Button */}
      <Button onClick={handleSearch} disabled={searching || !selectedFrom || !selectedTo || !dateFrom}
        className="w-full sm:w-auto bg-[var(--brand-ink)] hover:bg-black text-white font-medium px-6 py-2.5 rounded-lg transition-all">
        {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
        {searching ? "Suche läuft..." : "Flüge suchen"}
      </Button>

      {/* ===== PKW vs. FLUG VERGLEICH ===== */}
      {results && results.pkw_vergleich && !results.pkw_vergleich.error && results.offers && results.offers.length > 0 && (
        <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#f5f0eb] to-white border border-[var(--brand-border)]">
          <h4 className="text-sm font-semibold text-[var(--brand-ink)] mb-3 flex items-center gap-2">
            <TrendingDown className="w-4 h-4" /> Kostenvergleich
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {/* Flug */}
            <div className="p-3 rounded-lg bg-white border border-[var(--brand-border)] text-center">
              <Plane className="w-5 h-5 mx-auto mb-1 text-[var(--brand-ink)]" />
              <div className="text-lg font-bold text-[var(--brand-ink)]">{fmtPrice(results.offers[0].price, results.offers[0].currency)}</div>
              <div className="text-xs text-[var(--brand-muted)]">
                {results.offers[0].segments?.[0] && fmtDuration(results.offers[0].segments[0].duration)}
                {results.offers[0].stops === 0 && " · Direkt"}
                {results.offers[0].stops > 0 && ` · ${results.offers[0].stops} Stopp`}
              </div>
              <div className="text-xs font-medium text-[var(--brand-muted)] mt-1">Flug (Duffel)</div>
            </div>
            {/* PKW */}
            <div className="p-3 rounded-lg bg-white border border-[var(--brand-border)] text-center">
              <Car className="w-5 h-5 mx-auto mb-1 text-[var(--brand-ink)]" />
              <div className="text-lg font-bold text-[var(--brand-ink)]">{fmtPrice(results.pkw_vergleich.kosten)}</div>
              <div className="text-xs text-[var(--brand-muted)]">{results.pkw_vergleich.stunden}h · {results.pkw_vergleich.km} km</div>
              <div className="text-xs font-medium text-[var(--brand-muted)] mt-1">PKW ({results.pkw_vergleich.detail})</div>
            </div>
          </div>
          {results.vergleich_text && (
            <p className="text-xs text-center text-[var(--brand-muted)] mt-3 font-medium">{results.vergleich_text}</p>
          )}
        </div>
      )}

      {/* ===== DUFFEL LIVE-ERGEBNISSE ===== */}
      {searchMode === "duffel" && results && results.offers && results.offers.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[var(--brand-ink)]">
              {results.shown_offers} Flüge gefunden
            </p>
          </div>
          <div className="space-y-2">
            {results.offers.map((offer, idx) => {
              const firstSeg = offer.segments?.[0];
              const lastSeg = offer.segments?.[offer.segments.length - 1]; // Jetzt nur Hinflug-Segmente (Backend trennt nach Slices)
              const isSelected = selectedOffer?.id === offer.id;
              const isLiveOffer = offer.live_mode === true;
              const sourceLabel = isLiveOffer ? "Live-Angebot von Duffel" : "Duffel-Testangebot – nicht buchbar";
              return (
                <div key={offer.id || idx}
                  className={`rounded-xl border px-4 py-3.5 transition-all cursor-pointer ${isSelected ? "border-[var(--brand-ink)] bg-[#f5f0eb] shadow-sm" : "border-[var(--brand-border)] hover:border-[var(--brand-ink)] hover:bg-[#faf8f5]"}`}
                  onClick={() => setSelectedOffer(offer)}>
                  <div className="flex items-center justify-between gap-4">
                    {/* Fluginfo */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-ink)]">
                        <span>{firstSeg?.origin || ""}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-[var(--brand-muted)]" />
                        <span>{lastSeg?.destination || ""}</span>
                        {offer.stops === 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">Direkt</span>}
                        {offer.stops > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">{offer.stops} Stopp{offer.stops > 1 ? "s" : ""}</span>}
                      </div>
                      <div className="text-xs text-[var(--brand-muted)] mt-1.5 flex flex-wrap items-center gap-x-3">
                        {firstSeg && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(firstSeg.departure_time)} – {fmtTime(lastSeg?.arrival_time)}</span>}
                        {firstSeg?.duration && <span>{fmtDuration(firstSeg.duration)}</span>}
                        {firstSeg && <span className="font-medium">{firstSeg.airline_name}</span>}
                        {firstSeg?.flight_number && <span className="font-mono text-[10px]">{firstSeg.flight_number}</span>}
                      </div>
                    </div>
                    {/* Preis & Aktion */}
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-[var(--brand-ink)]">{fmtPrice(offer.price, offer.currency)}</div>
                      <div className="text-[10px] text-[var(--brand-muted)] uppercase tracking-wider">Gesamtpreis · {adults} Reisende · {tripScope}</div>
                      <div className={`mt-1 text-[10px] font-medium ${isLiveOffer ? "text-emerald-700" : "text-amber-700"}`}>{sourceLabel}</div>
                      <div className="mt-1 text-[10px] text-[var(--brand-muted)]">Abgerufen: {fmtTimestamp(results.retrieved_at)} · gültig bis: {fmtTimestamp(offer.expires_at)}</div>
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); handleSelectFlight(offer); }} disabled={!isLiveOffer}
                        className={`mt-2 h-7 text-xs rounded-md transition-all ${isSelected ? "bg-green-600 hover:bg-green-700 text-white" : "bg-[var(--brand-ink)] hover:bg-black text-white"}`}>
                        {isSelected ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Kosten übernommen</> : isLiveOffer ? "Als Reisekosten übernehmen" : "Nicht übernehmbar"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-[var(--brand-muted)]">Der Preis ist der von Duffel für diese Auswahl gelieferte Gesamtpreis. Er ist noch keine Buchung und kann sich bis zu einer späteren, erneuten Preisbestätigung ändern.</p>
          {/* Hinweis Chef-Freigabe */}
          {selectedOffer && tripId && (
            <div className="mt-4 p-3 rounded-lg bg-[#f5f0eb] border border-[var(--brand-border)] flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[var(--brand-ink)] mt-0.5 shrink-0" />
              <div className="text-xs text-[var(--brand-ink)]">
                <strong>Als Reisekosten übernommen – noch nicht gebucht:</strong> Es wurde weder reserviert noch bezahlt. Eine spätere Buchungsfunktion muss den Preis erneut beim selben Anbieter bestätigen.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Duffel: Fehler */}
      {searchMode === "duffel" && results && results.error && (!results.offers || results.offers.length === 0) && (
        <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-100 text-sm text-red-800 flex items-start gap-2">
          <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{results.error}</span>
        </div>
      )}

      {/* Duffel: Keine Ergebnisse */}
      {searchMode === "duffel" && results && results.offers && results.offers.length === 0 && !results.error && (
        <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-100 text-sm text-amber-800">
          Keine Flüge für diese Strecke/Datum gefunden. Versuchen Sie ein anderes Datum.
        </div>
      )}

      {/* ===== KIWI FALLBACK ===== */}
      {searchMode === "kiwi" && results && results.mode === "redirect" && (
        <div className="mt-5 p-4 rounded-xl bg-[#f5f0eb] border border-[var(--brand-border)]">
          <p className="text-sm text-[var(--brand-ink)] mb-2 font-medium">
            <Plane className="w-4 h-4 inline mr-1.5" />
            Preise direkt bei Kiwi prüfen
          </p>
          <p className="text-xs text-[var(--brand-muted)] mb-1">
            Kiwi ist ein eigenständiger Anbieter. Es wurde bewusst kein Preis aus der Suite an Kiwi übergeben oder als Kiwi-Preis angezeigt.
          </p>
          <p className="text-xs text-[var(--brand-muted)] mb-3">
            Suche: {results.search_parameters?.origin} → {results.search_parameters?.destination} · {results.search_parameters?.departure_date}{results.search_parameters?.return_date ? ` · Rückflug ${results.search_parameters.return_date}` : ""} · {results.search_parameters?.adults} Reisende.
          </p>
          <a href={results.deep_link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[var(--brand-ink)] text-white text-sm font-medium hover:bg-black transition no-underline">
            <ExternalLink className="w-4 h-4" /> Aktuelle Preise bei Kiwi prüfen
          </a>
        </div>
      )}
    </Card>
  );
};
